/**
 * The app's half of the course-frame protocol.
 *
 * Read alongside `protocol.ts`, which is the written agreement; this file is
 * the parent's implementation of it and nothing else. Review's
 * `components/rail/useCourseBus.ts` is the shape this follows.
 *
 * ── Three rules, each of them a real failure ────────────────────────────────
 *
 * 1. **Identity is the window handle.** The frame is sandboxed without
 *    `allow-same-origin`, so `e.origin` is the string `"null"` — shared by
 *    every sandboxed frame on the page, including a nested one a CustomCode
 *    block runs in. `isFrameMessage` checks `e.source === iframe.contentWindow`
 *    *and* the `source` field, and neither half is optional.
 *
 * 2. **One POST in flight, and the newest commit wins.** A commit is
 *    cumulative — `suspend`, `percent` and each lesson's `seconds` describe the
 *    whole session so far, and the server's seconds arithmetic subtracts what
 *    this `sessionKey` had already contributed before adding the new figure. So
 *    a commit that arrives while another is in flight *replaces* the one
 *    waiting rather than queueing behind it. Sending them all in parallel would
 *    race two monotone upserts against each other for no extra information.
 *
 * 3. **The last commit leaves on `pagehide`.** A learner who closes the tab
 *    mid-lesson has to keep their place. An ordinary `fetch` is cancelled when
 *    the document goes away; `keepalive` is what survives it, which is why this
 *    one call is built here from `request` rather than going through
 *    `learn.progress`. `pagehide` and not `beforeunload`: the latter does not
 *    fire on iOS, and a back/forward-cache restore fires `pageshow` after it,
 *    which is a page that keeps working.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type React from 'react';
import type { ProgressCommitWire } from '@wire';
import { B, errorText, learn, request } from '../api';
import {
  BRIDGE_SOURCE,
  isFrameMessage,
  type AppCommand,
  type BridgeLesson,
  type CommitMessage,
  type FrameMessage
} from './protocol';

export interface CourseBus {
  /** The frame has rendered and its buttons exist. No command before this. */
  ready: boolean;
  lessons: BridgeLesson[];
  /** The lesson on screen. `-1` on the course overview. */
  lessonIndex: number;
  /** Whether **Next** is open. The footer's Next and Mark complete are disabled
   *  while this is false. */
  gateOpen: boolean;
  /** The number the runtime is showing, mirrored into the app's header. */
  percent: number | null;
  /** Ask the frame to do one of the four things it accepts. */
  send: (command: AppCommand['type'], lessonId?: string) => void;
  /**
   * **Save and close**: ask for a commit, wait up to 300 ms for it, and post
   * whatever is outstanding. Resolves when there is nothing left to send, so
   * the caller can navigate straight after awaiting it.
   */
  flush: () => Promise<void>;
  /** A failed POST, in the words the server used. Progress is not lost — the
   *  next commit carries the same cumulative state. */
  error: string | null;
}

/** Strip the envelope: what the frame sends and what the route takes are the
 *  same object apart from these two fields. */
function body(message: CommitMessage): ProgressCommitWire {
  const { source: _source, type: _type, ...rest } = message;
  return rest;
}

export function useCourseBus(opts: {
  frame: React.RefObject<HTMLIFrameElement | null>;
  courseId: string;
  /** False in browse mode — a teacher looking at a draft records nothing. */
  record: boolean;
}): CourseBus {
  const { frame, courseId, record } = opts;

  const [ready, setReady] = useState(false);
  const [lessons, setLessons] = useState<BridgeLesson[]>([]);
  const [lessonIndex, setLessonIndex] = useState(-1);
  const [gateOpen, setGateOpen] = useState(false);
  const [percent, setPercent] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  /** The newest commit not yet accepted by the server, or null. */
  const pending = useRef<ProgressCommitWire | null>(null);
  /** The drain in progress, so a second caller joins it rather than starting a
   *  parallel one. */
  const draining = useRef<Promise<void> | null>(null);
  /** Resolvers waiting for the next commit — `flush`, at most one at a time. */
  const waiting = useRef<(() => void)[]>([]);
  /** `flush` reads this outside React's render, so it needs the ref and not
   *  the state: closing a course that never finished loading should not sit
   *  for 300 ms waiting on a frame that was never listening. */
  const listening = useRef(false);

  const drain = useCallback((): Promise<void> => {
    if (draining.current) return draining.current;

    const run = (async () => {
      while (pending.current) {
        const next = pending.current;
        pending.current = null;
        try {
          await learn.progress(courseId, next);
          setError(null);
        } catch (err) {
          setError(errorText(err, 'Your progress did not save.'));
          // Do not put it back: the next commit describes the same session
          // cumulatively, so retrying this one adds nothing a later one lacks.
        }
      }
      draining.current = null;
    })();

    draining.current = run;
    return run;
  }, [courseId]);

  useEffect(() => {
    function onMessage(e: MessageEvent) {
      if (!isFrameMessage(e, frame.current?.contentWindow)) return;
      const message = e.data as FrameMessage;

      if (message.type === 'ready') {
        listening.current = true;
        setReady(true);
        setLessons(message.lessons);
        setLessonIndex(message.lesson);
        setGateOpen(message.gateOpen);
      } else if (message.type === 'lesson') {
        setLessonIndex(message.index);
        setGateOpen(message.gateOpen);
      } else if (message.type === 'gate') {
        setGateOpen(message.gateOpen);
      } else if (message.type === 'commit') {
        setPercent(message.percent);
        if (record) {
          pending.current = body(message);
          void drain();
        }
        // Wake `flush` whether or not this one is being posted: browse mode
        // still answers the command, and a caller waiting on nothing hangs.
        const woken = waiting.current;
        waiting.current = [];
        for (const resolve of woken) resolve();
      }
    }

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [frame, record, drain]);

  const send = useCallback(
    (type: AppCommand['type'], lessonId?: string) => {
      const w = frame.current?.contentWindow;
      if (!w) return;
      const command: AppCommand =
        type === 'open'
          ? { source: BRIDGE_SOURCE, type: 'open', lessonId: lessonId ?? '' }
          : ({ source: BRIDGE_SOURCE, type } as AppCommand);
      // '*' because the frame's origin is opaque and cannot be named. The frame
      // filters on `source`; nothing secret travels this way.
      w.postMessage(command, '*');
    },
    [frame]
  );

  const flush = useCallback(async () => {
    if (listening.current) {
      send('flush');
      await new Promise<void>((resolve) => {
        waiting.current.push(resolve);
        // The cap is the plan's: a frame that has stopped answering must not
        // hold the close button open.
        window.setTimeout(resolve, 300);
      });
    }
    await drain();
  }, [send, drain]);

  // The tab going away, with something outstanding.
  useEffect(() => {
    if (!record) return;

    const onHide = () => {
      const outstanding = pending.current;
      if (!outstanding) return;
      pending.current = null;
      void request(`${B}/courses/${encodeURIComponent(courseId)}/progress`, {
        method: 'POST',
        body: JSON.stringify(outstanding),
        keepalive: true
      }).catch(() => {
        // The document is going; there is nobody left to tell.
      });
    };

    window.addEventListener('pagehide', onHide);
    return () => window.removeEventListener('pagehide', onHide);
  }, [courseId, record]);

  return { ready, lessons, lessonIndex, gateOpen, percent, send, flush, error };
}
